import { ExportContext } from './types';
import { downloadBlob, formatExportTimestamp } from './exportUtils';

export interface PdfExportOptions {
  orientation?: 'portrait' | 'landscape';
  /** Extra label/value lines rendered under the filter block (e.g. key stats). */
  notes?: string[];
}

const MARGIN = 32;
const TITLE_SIZE = 16;
const META_SIZE = 9;
const LINE_GAP = 13;

/**
 * jsPDF's built-in Helvetica drops typographic punctuation, which silently
 * swallows the em dashes in the scope label and the en dashes between period
 * dates. Fold them to ASCII before drawing.
 */
function pdfText(value: string): string {
  return value
    .replace(/[–—−]/g, '-')
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/•/g, '-');
}

/**
 * Exports a live DOM region as a PDF snapshot, prefixed with a header that
 * records the report title, who exported it, the role-based access scope, and
 * every active filter — so a recipient can tell what data the charts reflect.
 *
 * jsPDF + html2canvas are ~1MB combined and are loaded on demand.
 */
export async function exportElementToPdf(
  element: HTMLElement,
  ctx: ExportContext,
  options: PdfExportOptions = {}
): Promise<void> {
  const [{ jsPDF }, html2canvasMod] = await Promise.all([import('jspdf'), import('html2canvas')]);
  const html2canvas = (html2canvasMod as unknown as { default: typeof import('html2canvas').default })
    .default;

  const canvas = await html2canvas(element, {
    scale: Math.min(window.devicePixelRatio || 1, 2) * 1.5,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: element.scrollWidth,
  });

  const orientation = options.orientation ?? 'landscape';
  const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;

  const headerBottom = drawHeader(pdf, ctx, options, contentWidth);

  // ─── Paginate the snapshot ───────────────────────────────────────────────
  const imgWidth = contentWidth;
  const pxPerPt = canvas.width / imgWidth;

  // A sliver of space under the header reads as a rendering glitch — start the
  // snapshot on its own page instead.
  let topOffset = headerBottom;
  if (pageHeight - headerBottom - MARGIN < 120) {
    pdf.addPage();
    topOffset = MARGIN;
  }

  let sourceY = 0;
  while (sourceY < canvas.height) {
    const availableHeightPt = pageHeight - topOffset - MARGIN;
    const sliceHeightPx = Math.min(Math.ceil(availableHeightPt * pxPerPt), canvas.height - sourceY);
    const sliceDataUrl = sliceCanvas(canvas, sourceY, sliceHeightPx);

    pdf.addImage(sliceDataUrl, 'PNG', MARGIN, topOffset, imgWidth, sliceHeightPx / pxPerPt);

    sourceY += sliceHeightPx;
    if (sourceY < canvas.height) {
      pdf.addPage();
      topOffset = MARGIN;
    }
  }

  downloadBlob(pdf.output('blob'), `${ctx.fileName}.pdf`);
}

type JsPdf = import('jspdf').jsPDF;

/** Draws the title/meta/filter block and returns the y position below it. */
function drawHeader(
  pdf: JsPdf,
  ctx: ExportContext,
  options: PdfExportOptions,
  contentWidth: number
): number {
  let y = MARGIN + TITLE_SIZE;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(TITLE_SIZE);
  pdf.setTextColor(26, 59, 204); // #1A3BCC
  pdf.text(pdfText(ctx.title), MARGIN, y);
  y += 16;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(META_SIZE);
  pdf.setTextColor(85, 85, 85);

  const timestamp = formatExportTimestamp();
  pdf.text(
    pdfText(ctx.exportedBy ? `Exported ${timestamp} by ${ctx.exportedBy}` : `Exported ${timestamp}`),
    MARGIN,
    y
  );
  y += LINE_GAP;
  pdf.text(pdfText(`Access scope: ${ctx.scopeLabel}`), MARGIN, y);
  y += LINE_GAP + 2;

  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(17, 17, 17);
  pdf.text('Filters applied', MARGIN, y);
  y += LINE_GAP;

  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(51, 51, 51);

  const writeWrapped = (text: string) => {
    const lines = pdf.splitTextToSize(pdfText(text), contentWidth) as string[];
    for (const line of lines) {
      pdf.text(line, MARGIN, y);
      y += LINE_GAP;
    }
  };

  if (ctx.filters.length === 0) {
    writeWrapped('None');
  } else {
    for (const filter of ctx.filters) {
      writeWrapped(`${filter.label}: ${filter.value}`);
    }
  }

  for (const note of options.notes ?? []) {
    writeWrapped(note);
  }

  y += 6;
  pdf.setDrawColor(224, 224, 224);
  pdf.line(MARGIN, y, MARGIN + contentWidth, y);

  return y + 10;
}

/** Copies a horizontal band out of the source canvas as a PNG data URL. */
function sliceCanvas(source: HTMLCanvasElement, sourceY: number, sliceHeight: number): string {
  const slice = document.createElement('canvas');
  slice.width = source.width;
  slice.height = sliceHeight;
  const ctx2d = slice.getContext('2d');
  if (!ctx2d) throw new Error('Unable to render the PDF snapshot (2D canvas unavailable).');
  ctx2d.fillStyle = '#ffffff';
  ctx2d.fillRect(0, 0, slice.width, slice.height);
  ctx2d.drawImage(source, 0, sourceY, source.width, sliceHeight, 0, 0, source.width, sliceHeight);
  return slice.toDataURL('image/png');
}
